'use client';

import { ComponentType, FC, ReactNode, useMemo } from 'react';
import { LogOut } from 'lucide-react';
import { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@spikemark/ui/components/design-system/sidebar/sidebar';
import { Carousel, CarouselContent, CarouselDots, CarouselItem } from '@spikemark/ui/components/ui/carousel';
import { OfferTile } from '@spikemark/ui/components/design-system/offer-tile';
import { NavMenuItem } from '@spikemark/ui/components/design-system/sidebar/nav-menu-item';
import { NavItem, SidebarItem } from '@spikemark/ui/components/design-system/sidebar/types';
import { Separator } from '@spikemark/ui/components/design-system/separator';
import { Dialog, DialogTrigger } from '@spikemark/ui/components/ui/dialog';
import { useUserContext } from '@/providers/user-context-provider';
import { SiteOfferFlag } from '@/core/feature-flags';
import { EntitlementType } from '@spikemark/auth';
import { dialogContentMap } from './dialogs/constants';
const upgradeOfferTile: SiteOfferFlag = {
  dialogContent: 'upgrade-tier',
  tagIcon: 'gem',
  tagClassName: 'bg-pl-pink-100 text-pl-pink-900',
  tagTitle: 'Upgrade',
  className: 'bg-pl-pink-800',
  description: 'Ready to elevate your tournament stats? Unlock in-depth analytics and insights!',
  cta: 'Find out more',
  url: '#'
};
type AppSidebarProps = {
  menuItems: SidebarItem[];
  offers?: SiteOfferFlag[];
  footerLinks?: NavItem[];
  signOutButton: ComponentType<{
    children?: ReactNode;
  }>;
};
export const AppSidebar: FC<AppSidebarProps> = ({
  menuItems,
  offers = [],
  footerLinks = [],
  signOutButton: SignOutButton
}) => {
  const {
    activeTeamId,
    activeTeamPlan
  } = useUserContext();
  const sidebarOffers = useMemo(() => {
    const updatedOffers = [...offers];
    if (activeTeamPlan !== EntitlementType.ProAnalytics) {
      updatedOffers.push(upgradeOfferTile);
    }
    return updatedOffers;
  }, [activeTeamPlan, offers]);
  return <Sidebar data-sentry-element="Sidebar" data-sentry-component="AppSidebar" data-sentry-source-file="app-sidebar.tsx">
      <SidebarContent data-sentry-element="SidebarContent" data-sentry-source-file="app-sidebar.tsx">
        <SidebarGroup className="flex-1" data-sentry-element="SidebarGroup" data-sentry-source-file="app-sidebar.tsx">
          <SidebarGroupContent data-sentry-element="SidebarGroupContent" data-sentry-source-file="app-sidebar.tsx">
            <SidebarMenu data-sentry-element="SidebarMenu" data-sentry-source-file="app-sidebar.tsx">
              {menuItems.map((item, i) => <NavMenuItem item={item} key={`${item.title}${i}`} params={{
              '[teamId]': activeTeamId
            }} />)}
            </SidebarMenu>
          </SidebarGroupContent>
          {sidebarOffers.length > 0 ? <>
              <SidebarGroupContent className="flex-1 min-h-10" />
              <SidebarGroupContent>
                <Carousel className="relative overflow-hidden" opts={{
              align: 'start'
            }}>
                  <CarouselContent className="-ml-2">
                    {sidebarOffers.map((offer, i) => <CarouselItem key={`${offer.url}${i}`} className="pl-2">
                        <OfferTileWrapper dialogContent={offer.dialogContent}>
                          <OfferTile key={`${offer.url}${i}`} tagIcon={offer.tagIcon} tagTitle={offer.tagTitle} tagClassName={offer.tagClassName} className={offer.className} description={offer.description} cta={offer.cta} url={offer.url} />
                        </OfferTileWrapper>
                      </CarouselItem>)}
                  </CarouselContent>
                  {sidebarOffers.length > 1 && <CarouselDots variant="secondary" />}
                </Carousel>
              </SidebarGroupContent>
            </> : null}
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="p-0" data-sentry-element="SidebarFooter" data-sentry-source-file="app-sidebar.tsx">
        <Separator className="bg-white/20" data-sentry-element="Separator" data-sentry-source-file="app-sidebar.tsx" />
        <SidebarMenu className="p-2" data-sentry-element="SidebarMenu" data-sentry-source-file="app-sidebar.tsx">
          {footerLinks.map((item, i) => <NavMenuItem item={item} key={`${item.title}${i}`} />)}

          <SidebarMenuItem data-sentry-element="SidebarMenuItem" data-sentry-source-file="app-sidebar.tsx">
            <SignOutButton data-sentry-element="SignOutButton" data-sentry-source-file="app-sidebar.tsx">
              <SidebarMenuButton data-sentry-element="SidebarMenuButton" data-sentry-source-file="app-sidebar.tsx">
                <LogOut size={16} data-sentry-element="LogOut" data-sentry-source-file="app-sidebar.tsx" />
                <span className="font-medium">Sign Out</span>
              </SidebarMenuButton>
            </SignOutButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>;
};
const OfferTileWrapper: FC<{
  dialogContent?: string;
  children: React.ReactNode;
}> = ({
  dialogContent,
  children
}) => {
  const hasDialog = !!dialogContent;
  const {
    activeTeamPlan
  } = useUserContext();
  if (hasDialog) {
    const DialogContentComponent = dialogContentMap[dialogContent];
    const hasStandardAccess = activeTeamPlan === EntitlementType.QualifyingLiveScoring;
    return <Dialog>
        <DialogTrigger className="text-left">{children}</DialogTrigger>
        <DialogContentComponent hasStandardAccess={hasStandardAccess} />
      </Dialog>;
  }
  return children;
};